import {
  useCallback,
  useEffect,
  useRef,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react'
import toast from 'react-hot-toast'
import hydraulicDataConfig from '@/api/config/hydraulicDataConfig'
import type { GridApi } from '@/components/grid/types'
import { commitOpenEditor } from '@/components/grid/commitOpenEditor'
import type { GuidedSaveResult } from '@/features/config/contexts/guided-edit-context'
import { useIsGuidedSetup } from '@/features/config/contexts/guided-edit-context'
import { getApiErrorMessage } from '@/helpers/notify'
import {
  DESCRIPTION_FIELD,
  SAVE_REQUEST_GAP_MS,
  SAVE_RETRY_DELAY_MS,
  SAVE_RETRY_LIMIT,
} from './constants'
import {
  findCreatedPipeId,
  newPipeIdFromResponse,
  orderParentsFirst,
  sleep,
} from './pipe-writes'
import {
  cellRowId,
  findPipeProblems,
  isRetryableSaveError,
  nextInvalidCells,
} from './save-rules'
import { isDraftId } from './tree-data'
import type { DraftPipe, HydraulicTreeRow } from './types'

interface UseHydraulicSaveArgs {
  gridApiRef: MutableRefObject<GridApi<HydraulicTreeRow> | null>
  draftsRef: MutableRefObject<DraftPipe[]>
  setDrafts: Dispatch<SetStateAction<DraftPipe[]>>
  setInvalidCells: Dispatch<SetStateAction<Set<string>>>
  resetDrafts: () => void
  editedRowsRef: MutableRefObject<Record<string, any>>
  editedCellsRef: MutableRefObject<Set<string>>
  bumpEditVersion: () => void
  gridData: HydraulicTreeRow[]
  getViewport: () => HTMLElement | null
  authInterfaceId: unknown
  onRefetch?: () => Promise<any>
  onToggleBulkEdit?: () => void
  /**
   * SCRUM-1032: the guided setup stays in bulk edit for the whole step. Saving
   * is something that happens *while* entering data there — on a pause, and on
   * Next — not the act of finishing, so dropping back to read-only after every
   * write would take the editors away mid-entry (and with them the "+ Add Pipe"
   * row, leaving an empty step with no way to add the first pipe).
   */
  stayInBulkEdit?: boolean
  t: (key: string, options?: any) => string
}

/**
 * The toolbar's Save and Cancel: the staged drafts and the edited rows are
 * committed together, because creating a pipe is a pending change like any
 * other.
 */
/**
 * One toast id for every save error this grid raises, so a repeat replaces the
 * one on screen instead of stacking under it.
 */
export const HYDRAULIC_SAVE_TOAST_ID = 'hydraulic-save-error'

interface LandedDraft {
  /** Null until read back, when the create response did not name it. */
  id: number | null
  interfaceId: number
  parentId: number
  description: string
  /** Pipes that existed before it, so the read-back cannot pick one. */
  excludeIds: ReadonlySet<number>
}

interface SaveOptions {
  /** Pause autosave: leave new pipes staged while any is incomplete. */
  holdIncompleteDrafts?: boolean
}

const useHydraulicSave = ({
  gridApiRef,
  draftsRef,
  setDrafts,
  setInvalidCells,
  resetDrafts,
  editedRowsRef,
  editedCellsRef,
  bumpEditVersion,
  gridData,
  getViewport,
  authInterfaceId,
  onRefetch,
  onToggleBulkEdit,
  stayInBulkEdit = false,
  t,
}: UseHydraulicSaveArgs) => {
  const pendingScrollRef = useRef<number | null>(null)
  // Read inside a save, which outlives the render it started in.
  const gridDataRef = useRef(gridData)
  gridDataRef.current = gridData
  /**
   * Drafts that have been created, by draft id: what is needed to find the
   * pipe's real id when a sub-pipe staged under it asks. Kept across runs, so
   * a sub-pipe added while its parent was being written still finds it on
   * the next save.
   */
  const landedDraftsRef = useRef(new Map<number, LandedDraft>())

  // Restore scroll after a refetch
  useEffect(() => {
    if (pendingScrollRef.current === null) return
    const scrollTo = pendingScrollRef.current
    pendingScrollRef.current = null
    const timeoutId = setTimeout(() => {
      const viewport = getViewport()
      if (viewport) viewport.scrollTop = scrollTo
    }, 50)
    return () => clearTimeout(timeoutId)
  }, [gridData, getViewport])

  const isGuidedSetup = useIsGuidedSetup()

  const performSave = useCallback(async (
    options: SaveOptions = {},
  ): Promise<GuidedSaveResult> => {
    // Commits the open editor before the staged rows are read. Not a
    // `sleep(0)`: a macrotask is throttled to one callback a minute in a
    // hidden tab, which parked the save (SCRUM-1032 QC, 6 Oct).
    commitOpenEditor(gridApiRef.current)

    const allDrafts = draftsRef.current.map((draft) => ({
      ...draft,
      description: draft.description.trim(),
    }))
    /**
     * SCRUM-1032 round 7 review (H8) — the pause autosave holds new pipes
     * back while any of them is still being filled in, without a toast or a
     * mark: complaining every few seconds at a half-typed row is not help.
     * It used to hold the whole save, so one blank new pipe also stopped
     * edits to existing pipes from being written. Those still go now; Save
     * and Next (no `holdIncompleteDrafts`) check every row as before.
     */
    const draftProblems = findPipeProblems(allDrafts)
    const holdingDrafts =
      Boolean(options.holdIncompleteDrafts) && draftProblems.size > 0
    const stagedDrafts = holdingDrafts ? [] : allDrafts
    /**
     * Round 7 follow-up — a held new pipe with a negative capacity is said at
     * once. It is a finished value the server will refuse, not a row still
     * being typed, and held silently it said nothing until Next. Only those
     * cells are marked: the other held pipes may still be mid-entry, and the
     * edits to existing pipes are still written.
     */
    const negativeDraftCells = holdingDrafts
      ? Array.from(draftProblems.entries())
          .filter(([, problem]) => problem === 'capacityNegative')
          .map(([key]) => key)
      : []
    const negativeDraftRows = new Set(
      negativeDraftCells.map((key) => cellRowId(key)),
    ).size
    const markInvalid = (marks: Iterable<string>) =>
      setInvalidCells((previous) =>
        nextInvalidCells(previous, marks, holdingDrafts, (id) => isDraftId(Number(id))),
      )
    const sayNegativeDrafts = () => {
      if (negativeDraftCells.length === 0) return
      toast.error(
        `${t('hydraulicColumnCapacity')} ${t('commonFormsValidationCannotBeNegative')}`,
        { id: HYDRAULIC_SAVE_TOAST_ID },
      )
    }
    const editedRows = Object.values(editedRowsRef.current)

    // Every staged row — new pipes and edits to existing ones alike — is
    // validated before anything is written: a half-applied save would leave
    // the user guessing which rows landed. Edits used to skip this, so a
    // capacity of -10 went to the server and came back as a 400 that was
    // then retried for ever (SCRUM-1032 QC round 7, items 1 and 3).
    const problems = findPipeProblems([...stagedDrafts, ...editedRows])

    if (problems.size > 0) {
      const reasons = new Set(problems.values())
      if (negativeDraftCells.length > 0) reasons.add('capacityNegative')
      markInvalid([...problems.keys(), ...negativeDraftCells])
      toast.error(
        // The value typed wrong first: it is why this toast went up now.
        reasons.has('capacityNegative')
          ? `${t('hydraulicColumnCapacity')} ${t('commonFormsValidationCannotBeNegative')}`
          : reasons.has('descriptionMissing')
            ? `${t('hydraulicColumnDescription')} ${t('commonFormsValidationIsRequired')}`
            : `${t('hydraulicColumnCapacity')} ${t('commonFormsValidationIsRequired')}`,
        { id: HYDRAULIC_SAVE_TOAST_ID },
      )
      /**
       * SCRUM-1032 QC round 6, item 4 — a non-zero `failed` is what holds the
       * wizard on this step (`runGuidedSaves`).
       *
       * This branch used to `return` undefined. `runGuidedSaves` reads
       * `result?.failed ?? 0`, so a refused save was indistinguishable from a
       * clean one: the toast went up and the wizard walked straight on to ET
       * Reference, taking the staged pipe with it — "error message shown
       * 'Capacity Must be greater than 0' → able go to ET Reference → back to
       * hydraulic → editted not save" (Wei Woon, 6 Oct).
       */
      return {
        // Rows, not cells: a new pipe missing both name and capacity is one
        // change the user has to fix, and the wizard says it as a count.
        failed:
          new Set(Array.from(problems.keys(), (key) => cellRowId(key)))
            .size + negativeDraftRows,
        total: stagedDrafts.length + editedRows.length + negativeDraftRows,
      }
    }
    // Existing pipes passed; a held draft keeps any mark an explicit Save
    // gave it, and a negative one is marked now.
    markInvalid(negativeDraftCells)
    sayNegativeDrafts()

    if (stagedDrafts.length === 0 && editedRows.length === 0) {
      if (negativeDraftRows > 0) {
        return { failed: negativeDraftRows, total: negativeDraftRows }
      }
      // The wizard flushes every grid on Next and stays in bulk edit, so a
      // step the user only passed through keeps its editor open.
      if (!stayInBulkEdit) {
        onToggleBulkEdit?.()
      }
      return { failed: 0, total: 0 }
    }

    const scrollTop = getViewport()?.scrollTop ?? 0
    const total = stagedDrafts.length + editedRows.length
    let failed = 0
    let lastError: any = null

    // One write at a time, with a retry: this API returns 500 when pipe
    // writes arrive concurrently, so a Promise.all over the batch lost most
    // of it and reported a single error for the whole thing. Only a 5xx or a
    // dropped connection is retried — a 400 is the value being refused, and
    // asking again gets the same answer.
    const runWithRetry = async <T,>(
      task: () => Promise<T>,
    ): Promise<{ ok: true; value: T } | { ok: false; value: null }> => {
      for (let attempt = 0; attempt < SAVE_RETRY_LIMIT; attempt++) {
        try {
          return { ok: true, value: await task() }
        } catch (error: any) {
          lastError = error
          if (!isRetryableSaveError(error)) break
          if (attempt < SAVE_RETRY_LIMIT - 1) await sleep(SAVE_RETRY_DELAY_MS)
        }
      }
      return { ok: false, value: null }
    }

    for (const row of editedRows) {
      const updated = await runWithRetry(() =>
        hydraulicDataConfig.update({
          interfaceId: row.interfaceId || Number(authInterfaceId),
          id: row.id,
          parentId: row.parentId,
          description: row.description,
          capacity: Number(row.capacity),
        }),
      )
      const id = String(row.id)
      if (!updated.ok) {
        // Left staged, so its cells stay amber and the edit can be retried.
        failed += 1
      } else if (editedRowsRef.current[id] === row) {
        // Retired only if it is still the value that was sent. An edit made
        // while this request was in flight replaced the entry with a new
        // object, and that one has not been written yet.
        delete editedRowsRef.current[id]
      }
      await sleep(SAVE_REQUEST_GAP_MS)
    }

    // SCRUM-1032 QC round 7, item 4 — the pending set is never replaced
    // wholesale. It used to be overwritten with the rows that failed, as they
    // were when the save started, which threw away a correction typed while
    // the save ran: the old -10 kept being sent, and the row stayed on
    // "Saving..." until a reload. Pending marks follow the current set.
    editedCellsRef.current = new Set(
      [...editedCellsRef.current].filter(
        (key) => editedRowsRef.current[cellRowId(key)],
      ),
    )
    // Repaint only: the autosave is armed by user edits, not by this.
    bumpEditVersion()

    // A draft sub-pipe needs its parent's REAL id, and `POST /api/pipe` takes
    // one record with one numeric parentId — there is no inline-children
    // payload. So parents are created first and each child's parentId is
    // resolved from what its parent's create produced.
    const orderedDrafts = orderParentsFirst(stagedDrafts)
    // Draft ids start over after a Cancel. A staged draft is never one that
    // landed, so an old entry under its id is about a different pipe — and
    // would hang this draft's sub-pipes off it if this draft's create failed.
    draftsRef.current.forEach((draft) => landedDraftsRef.current.delete(draft.id))
    /** Drafts this run created: only these leave the staged list. */
    const createdDraftIds = new Set<number>()
    /** Drafts kept staged, re-pointed at a parent that did land. */
    const restagedParentId = new Map<number, number>()
    /** Draft sub-pipes whose parent is gone for good: marked, not attempted. */
    const orphanCells: string[] = []
    // Pipes that already exist are never mistaken for the one a create made.
    // Pipes this run creates join the set as they land, so two new pipes with
    // the same name under one parent are not taken for each other.
    const knownPipeIds = new Set<number>(
      gridDataRef.current.map((row) => Number(row.id)).filter((id) => id > 0),
    )
    const lookUpCreated = (draft: DraftPipe, parentId: number) =>
      findCreatedPipeId({
        interfaceId: draft.interfaceId || Number(authInterfaceId),
        parentId,
        description: draft.description,
        excludeIds: knownPipeIds,
      })

    /**
     * The real id of a draft that has been created, read back on demand.
     *
     * Asked for whenever a staged pipe names that draft as its parent —
     * including one added while the parent's POST was still out (a minute on
     * a slow controller). Deciding up front, from the drafts this run started
     * with, left such a sub-pipe pointing at a parent that no longer existed
     * as a draft, and it could never be saved (SCRUM-1032 review).
     */
    const resolveLandedId = async (draftId: number): Promise<number | undefined> => {
      const landed = landedDraftsRef.current.get(draftId)
      if (!landed) return undefined
      if (landed.id === null) {
        const lookup = await runWithRetry(() =>
          findCreatedPipeId({
            interfaceId: landed.interfaceId,
            parentId: landed.parentId,
            description: landed.description,
            excludeIds: landed.excludeIds,
          }),
        )
        if (lookup.ok && lookup.value !== null) {
          landed.id = lookup.value
          knownPipeIds.add(lookup.value)
        }
      }
      return landed.id ?? undefined
    }

    /**
     * One create, retried on a 5xx or a dropped connection — but only once a
     * list read says it did not land. On a slow controller the write often
     * completes after the request has given up, and a blind retry made the
     * pipe twice. A failed read stops the retry: not knowing is not "no".
     */
    const createPipe = async (
      draft: DraftPipe,
      parentId: number,
    ): Promise<{ ok: true; id: number | null } | { ok: false }> => {
      for (let attempt = 0; attempt < SAVE_RETRY_LIMIT; attempt++) {
        try {
          const response = await hydraulicDataConfig.create({
            interfaceId: draft.interfaceId || Number(authInterfaceId),
            parentId,
            description: draft.description,
            capacity: Number(draft.capacity),
          })
          return { ok: true, id: newPipeIdFromResponse(response) }
        } catch (error: any) {
          lastError = error
          if (!isRetryableSaveError(error)) return { ok: false }
          await sleep(SAVE_RETRY_DELAY_MS)
          // Looked for after the last attempt too: a draft left staged after
          // a write that did land is made again by the next save.
          try {
            const landedId = await lookUpCreated(draft, parentId)
            if (landedId !== null) return { ok: true, id: landedId }
          } catch {
            return { ok: false }
          }
        }
      }
      return { ok: false }
    }

    for (const draft of orderedDrafts) {
      const parentId = isDraftId(draft.parentId)
        ? await resolveLandedId(draft.parentId)
        : draft.parentId

      // The parent's create failed, or its new id could not be read back.
      // Its descendants are not attempted, and stay staged along with it.
      if (parentId === undefined) {
        failed += 1
        // A parent still staged speaks for itself (its own failure, or it
        // is held). One that is neither staged nor landed never will.
        const parentStaged = draftsRef.current.some(
          (candidate) => candidate.id === draft.parentId,
        )
        if (!parentStaged) orphanCells.push(`${draft.id}::${DESCRIPTION_FIELD}`)
        continue
      }

      const created = await createPipe(draft, parentId)

      if (!created.ok) {
        failed += 1
        // Re-staged against the parent that DID land, so a retry cannot
        // create that parent a second time.
        if (parentId !== draft.parentId) restagedParentId.set(draft.id, parentId)
        await sleep(SAVE_REQUEST_GAP_MS)
        continue
      }

      createdDraftIds.add(draft.id)
      // Kept so a sub-pipe staged under it — now or while this run is still
      // going — can be pointed at its real id. The id is only read back when
      // one asks: the lookup costs a list read.
      landedDraftsRef.current.set(draft.id, {
        id: created.id,
        interfaceId: draft.interfaceId || Number(authInterfaceId),
        parentId,
        description: draft.description,
        excludeIds: new Set(knownPipeIds),
      })
      if (created.id !== null) knownPipeIds.add(created.id)

      await sleep(SAVE_REQUEST_GAP_MS)
    }

    // Sub-pipes staged while the requests were out, under a pipe that has
    // since landed, get its real id now.
    for (const draft of draftsRef.current) {
      if (
        !createdDraftIds.has(draft.id) &&
        isDraftId(draft.parentId) &&
        landedDraftsRef.current.has(draft.parentId)
      ) {
        await resolveLandedId(draft.parentId)
      }
    }

    // Only the drafts that landed leave the staged list: re-staging them would
    // create them a second time on the next Save. Taken out of the list as it
    // is NOW, not replaced with the one this run started from — a pipe added,
    // or a draft corrected, while the requests were out survives (round 7
    // review, H1). A draft staged under a parent that landed is re-pointed at
    // its real id. Written through the ref as well as the state, because the
    // refetch below can rebuild the rows before React has applied the state.
    const remainingDrafts = draftsRef.current
      .filter((draft) => !createdDraftIds.has(draft.id))
      .map((draft) => {
        const parentId =
          restagedParentId.get(draft.id) ??
          (isDraftId(draft.parentId)
            ? (landedDraftsRef.current.get(draft.parentId)?.id ?? undefined)
            : undefined)
        return parentId === undefined ? draft : { ...draft, parentId }
      })
    draftsRef.current = remainingDrafts
    setDrafts(remainingDrafts)

    if (orphanCells.length > 0) {
      setInvalidCells((previous) => new Set([...previous, ...orphanCells]))
    }

    // Every failure without a server error is a sub-pipe whose parent could
    // not be saved or found; that is what it is told, not "Something went
    // wrong".
    const reason = lastError
      ? getApiErrorMessage(lastError, t('somethingWentWrong'))
      : t('hydraulicParentPipeNotSaved')
    if (failed === 0) {
      // SCRUM-1032: inside the wizard the step shows one status for its
      // grids, so the pause autosave does not raise a toast per pipe. On
      // /config, Save is a deliberate act and still confirms.
      if (!isGuidedSetup) toast.success(t('updateSuccess'))
    } else if (failed === total) {
      toast.error(reason, { id: HYDRAULIC_SAVE_TOAST_ID })
    } else {
      toast.error(
        `${t('bulkSavePartialError', { failed, total })} ${reason}`,
        { id: HYDRAULIC_SAVE_TOAST_ID },
      )
    }

    if (onRefetch) {
      pendingScrollRef.current = scrollTop
      await onRefetch()
    }

    // Stay in bulk edit when something failed, so it can be retried.
    if (failed === 0 && !stayInBulkEdit) onToggleBulkEdit?.()

    return {
      failed: failed + negativeDraftRows,
      total: total + negativeDraftRows,
    }
  }, [
    gridApiRef,
    draftsRef,
    setDrafts,
    setInvalidCells,
    editedRowsRef,
    editedCellsRef,
    bumpEditVersion,
    onToggleBulkEdit,
    stayInBulkEdit,
    onRefetch,
    getViewport,
    authInterfaceId,
    t,
  ])

  /**
   * SCRUM-1032: saves run one at a time.
   *
   * The staged drafts are only cleared once every create has come back, so two
   * overlapping runs both read the same batch and POST it twice — which is how
   * a pipe ended up in the tree twice. In the guided setup there are two
   * triggers that can overlap: the pause autosave, and the wizard flushing
   * every grid on Next. Chaining makes the second run start from what the
   * first left behind, where a completed draft is no longer staged and the
   * early return takes it.
   */
  const inFlightRef = useRef<Promise<GuidedSaveResult> | null>(null)

  const handleSave = useCallback((options?: SaveOptions) => {
    // `catch` before the chain, so one failed run cannot poison the next.
    const run = (inFlightRef.current ?? Promise.resolve())
      .catch(() => {})
      .then(() => performSave(options))
      .finally(() => {
        if (inFlightRef.current === run) inFlightRef.current = null
      })
    inFlightRef.current = run
    return run
  }, [performSave])

  /** True while a save is running, so the autosave timer can hold off. */
  const isSaving = useCallback(() => inFlightRef.current !== null, [])

  const handleCancel = useCallback(() => {
    editedRowsRef.current = {}
    editedCellsRef.current.clear()
    bumpEditVersion()
    resetDrafts()
    if (onRefetch) {
      pendingScrollRef.current = getViewport()?.scrollTop ?? 0
      onRefetch()
    }
  }, [
    editedRowsRef,
    editedCellsRef,
    bumpEditVersion,
    resetDrafts,
    getViewport,
    onRefetch,
  ])

  return { handleSave, handleCancel, isSaving }
}

export default useHydraulicSave
